import { useCallback, useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { upsertCardState } from '../services/cardStateService';

export type PinKey = 'notes' | 'checklist' | 'subtasks';
export interface PinState { notes: boolean; checklist: boolean; subtasks: boolean }

const DEFAULT: PinState = { notes: false, checklist: false, subtasks: false };
const EVENT_NAME = 'dagrun:cardpin:change';

// Server state cache — populated by the board's one-shot loader and
// mutated in place on toggles so repeated toggles within a session don't
// re-fetch the existing row id.
type ServerEntry = { id?: string; noteExpanded: boolean; checklistExpanded: boolean };
const serverCache = new Map<string, ServerEntry>();
function serverKey(userId: string, taskId: string): string {
  return `${userId || 'anon'}::${taskId}`;
}

// Called by the board once per mount after fetching the user's card-state
// set in a single query. Primes the server cache so subsequent reads
// hit a fully populated map and server state wins over stale localStorage.
export function primeServerPinState(userId: string, entries: Map<string, { id: string; noteExpanded: boolean; checklistExpanded: boolean }>): void {
  entries.forEach((v, taskId) => {
    serverCache.set(serverKey(userId, taskId), { id: v.id, noteExpanded: v.noteExpanded, checklistExpanded: v.checklistExpanded });
    // Mirror into localStorage so card mounts can optimistically restore
    // without waiting for the server fetch on subsequent navigations.
    const prev = readLocalStoragePinState(userId, taskId);
    writeLocalStoragePinState(userId, taskId, { ...prev, notes: v.noteExpanded, checklist: v.checklistExpanded });
  });
  // Broadcast so all mounted TaskCards re-read.
  window.dispatchEvent(new Event(EVENT_NAME));
}

function storageKey(userId: string, taskId: string): string {
  return `dagrun.cardpin.${userId || 'anon'}.${taskId}`;
}

function readLocalStoragePinState(userId: string, taskId: string): PinState {
  try {
    const raw = localStorage.getItem(storageKey(userId, taskId));
    if (!raw) return { ...DEFAULT };
    const parsed = JSON.parse(raw) as Partial<PinState>;
    return { ...DEFAULT, ...parsed };
  } catch { return { ...DEFAULT }; }
}

function writeLocalStoragePinState(userId: string, taskId: string, state: PinState): void {
  try { localStorage.setItem(storageKey(userId, taskId), JSON.stringify(state)); } catch { /* ignore quota */ }
}

export function readPinState(userId: string, taskId: string): PinState {
  // Prefer server cache when populated; otherwise fall back to localStorage.
  const srv = serverCache.get(serverKey(userId, taskId));
  if (srv) {
    return { ...DEFAULT, notes: srv.noteExpanded, checklist: srv.checklistExpanded };
  }
  return readLocalStoragePinState(userId, taskId);
}

export function writePinState(userId: string, taskId: string, state: PinState): void {
  writeLocalStoragePinState(userId, taskId, state);
  window.dispatchEvent(new CustomEvent<{ taskId: string; state: PinState }>(EVENT_NAME, { detail: { taskId, state } }));
}

// OID lookup — synchronous from MSAL account cache. Single source of truth.
export function getMyOid(pca: IPublicClientApplication | undefined): string {
  if (!pca) return '';
  const acc = pca.getAllAccounts()[0];
  if (!acc) return '';
  return acc.localAccountId ?? acc.homeAccountId?.split('.')[0] ?? '';
}

export function usePinState(
  userId: string,
  taskId: string,
  pca?: IPublicClientApplication,
): [PinState, (key: PinKey, value: boolean) => void] {
  const [state, setState] = useState<PinState>(() => readPinState(userId, taskId));

  useEffect(() => { setState(readPinState(userId, taskId)); }, [userId, taskId]);

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ taskId: string; state: PinState }>;
      // Re-read from server cache / localStorage. Use the event detail if
      // provided, otherwise re-query readPinState() which now hits the
      // populated server cache after primeServerPinState.
      if (ce.detail?.taskId) {
        if (ce.detail.taskId === taskId) setState(ce.detail.state);
      } else {
        setState(readPinState(userId, taskId));
      }
    };
    window.addEventListener(EVENT_NAME, handler);
    return () => window.removeEventListener(EVENT_NAME, handler);
  }, [userId, taskId]);

  const toggle = useCallback((key: PinKey, value: boolean) => {
    setState(prev => {
      const next = { ...prev, [key]: value };
      writePinState(userId, taskId, next);
      // Fire-and-forget server write, and update the server cache so
      // subsequent reads don't regress to localStorage defaults.
      if (pca && userId) {
        const cacheKey = serverKey(userId, taskId);
        const existing = serverCache.get(cacheKey);
        // Subtasks state is intentionally not persisted server-side (not
        // in the schema) — only notes + checklist expand states travel.
        upsertCardState(pca, userId, taskId, next.notes, next.checklist, existing?.id)
          .then(id => {
            if (id) serverCache.set(cacheKey, { id, noteExpanded: next.notes, checklistExpanded: next.checklist });
          })
          .catch(() => { /* best effort */ });
      }
      return next;
    });
  }, [userId, taskId, pca]);

  return [state, toggle];
}
